import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

/**
 * Discriminator for the type of graph being rendered. The original API
 * uses untyped strings ("clo", "alo", "dyna" etc.) so we keep the type
 * as `string` to remain backward compatible.
 */
export type GraphType = string;

type GraphStateValue =
  | string
  | number
  | boolean
  | Record<string, unknown>
  | unknown[]
  | null
  | undefined;

interface DynaGraphPoint {
  dyanXTime?: string | number;
  dyanYOutput?: string | number;
  dynaZfadeTime?: string | number;
  [key: string]: unknown;
}

interface CloGraphPoint {
  hour?: number;
  power?: number;
  [key: string]: string | number | boolean | null | undefined;
}

export interface GraphPayload {
  graphPoints: CloGraphPoint[];
  graphType: GraphType;
  [key: string]: unknown;
}

/**
 * GraphStateService
 * --------------------------------------------------------------------
 * Owns every reactive stream that drives the various graph widgets
 * (CLO, Dyna, ALO, etc.) — previously inline on `ApiService`.
 *
 * NOTE on typing: kept permissive to match legacy contract.
 */
@Injectable({ providedIn: "root" })
export class GraphStateService {
  // --- Plain graph payloads ----------------------------------------
  readonly graphData = new BehaviorSubject<GraphPayload | "">("");
  readonly getGraphData: Observable<GraphPayload | ""> = this.graphData.asObservable();

  readonly cloGraphData = new BehaviorSubject<GraphPayload | "">("");
  readonly getCloGraphData: Observable<GraphPayload | ""> = this.cloGraphData.asObservable();

  readonly graphDynaData = new BehaviorSubject<DynaGraphPoint[]>([]);
  readonly getGraphDynaData: Observable<DynaGraphPoint[]> = this.graphDynaData.asObservable();

  // --- Per-feature enable toggles -----------------------------------
  readonly enableGraph = new BehaviorSubject<GraphStateValue>(null);
  readonly checkEnableGraph: Observable<GraphStateValue> = this.enableGraph.asObservable();

  readonly enableCloLiteGraph = new BehaviorSubject<GraphStateValue>(false);
  readonly checkCloLiteEnableGraph: Observable<GraphStateValue> = this.enableCloLiteGraph.asObservable();

  readonly enableDynaGraph = new BehaviorSubject<GraphStateValue>(false);

  readonly cloSchema = new BehaviorSubject<GraphStateValue>(false);
  readonly getCloSchema: Observable<GraphStateValue> = this.cloSchema.asObservable();

  // --- Graph validity / edit state ---------------------------------
  readonly isGraphValid = new BehaviorSubject<string>("");
  readonly checkGraphIsValid: Observable<string> = this.isGraphValid.asObservable();

  readonly graphIsEditedData = new BehaviorSubject<CloGraphPoint[]>([]);
  readonly checkGraphValueEdited: Observable<CloGraphPoint[]> = this.graphIsEditedData.asObservable();

  readonly changedGraphData = new BehaviorSubject<CloGraphPoint[]>([]);
  readonly getChangedGraphData: Observable<CloGraphPoint[]> = this.changedGraphData.asObservable();

  // --- Per-graph rendering hints -----------------------------------
  readonly graphRepresentation = new BehaviorSubject<string>("");
  readonly getGraphRepresentation: Observable<string> = this.graphRepresentation.asObservable();

  readonly graphDynaRepresentation = new BehaviorSubject<string>("");

  readonly graphLiteValue = new BehaviorSubject<string | CloGraphPoint[]>("");

  // --- OTP graph data -----------------------------------------------
  readonly otpFtpGraphData = new BehaviorSubject<{dimStart: number, dimStop: number, minDimLevel?: number} | null>(null);
  readonly getOtpFtpGraphData: Observable<{dimStart: number, dimStop: number, minDimLevel?: number} | null> =
    this.otpFtpGraphData.asObservable();

  readonly otpDtpGraphData = new BehaviorSubject<{dimStart: number, dimStop: number} | null>(null);
  readonly getOtpDtpGraphData: Observable<{dimStart: number, dimStop: number} | null> =
    this.otpDtpGraphData.asObservable();

  // --- Setters (names match legacy ApiService) ---------------------
  sendGraphData(graphType: GraphType, data: CloGraphPoint[]): void {
    const payload: GraphPayload = { graphType, graphPoints: data };
    this.graphData.next(payload);
  }

  sendCloGraphData(graphType: GraphType, data: CloGraphPoint[]): void {
    const payload: GraphPayload = { graphType, graphPoints: data };
    this.cloGraphData.next(payload);
  }

  sendDynaGraphData(data: DynaGraphPoint[]): void {
    this.graphDynaData.next(data);
  }

  updateGraphData(data: CloGraphPoint[]): void {
    this.changedGraphData.next(data);
  }
  setEnableGraph(data: GraphStateValue): void {
    this.enableGraph.next(data);
  }
  setCloLiteEnableGraph(data: GraphStateValue): void {
    this.enableCloLiteGraph.next(data);
  }
  setEnableDynaGraph(data: GraphStateValue): void {
    this.enableDynaGraph.next(data);
  }

  sendGraphValidData(data: string): void {
    this.isGraphValid.next(data);
  }
  sendGraphIsEditedData(data: CloGraphPoint[]): void {
    this.graphIsEditedData.next(data);
  }

  setGraphRepresentation(data: string): void {
    this.graphRepresentation.next(data);
  }
  setDynaGraphRepresentation(data: string): void {
    this.graphDynaRepresentation.next(data);
  }

  sendGraphValues(data: string | CloGraphPoint[]): void {
    this.graphLiteValue.next(data);
  }

  setCloShema(data: GraphStateValue): void {
    this.cloSchema.next(data);
  }
}
